import {useSyncExternalStore} from 'react'

type MediaQueryStore = {
  mediaQueryList: MediaQueryList
  listeners: Set<() => void>
  onChange: () => void
}

const mediaQueryStores = new Map<string, MediaQueryStore>()

const getMediaQueryStore = (query: string) => {
  let store = mediaQueryStores.get(query)

  if (!store) {
    const mediaQueryList = window.matchMedia(query)
    const listeners = new Set<() => void>()
    store = {
      mediaQueryList,
      listeners,
      onChange: () => listeners.forEach((listener) => listener()),
    }
    mediaQueryStores.set(query, store)
  }

  return store
}

// A browser or test without matchMedia (jsdom) reads every query as false instead of failing (507).
const canMatchMedia = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'

const useMediaQuery = (query: string) => {
  const subscribe = (listener: () => void) => {
    if (!canMatchMedia()) return () => undefined

    const store = getMediaQueryStore(query)
    if (store.listeners.size === 0) {
      store.mediaQueryList.addEventListener('change', store.onChange)
    }
    store.listeners.add(listener)

    return () => {
      store.listeners.delete(listener)
      if (store.listeners.size === 0) {
        store.mediaQueryList.removeEventListener('change', store.onChange)
      }
    }
  }

  const getSnapshot = () => (canMatchMedia() ? getMediaQueryStore(query).mediaQueryList.matches : false)

  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

const useIsMobileViewport = () => useMediaQuery('(max-width: 767.98px)')

export {useIsMobileViewport, useMediaQuery}
